import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, Pencil, Wand2, Mail } from "lucide-react";
import { api, apiError, money, fxNote, API } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Field, PeriodPicker, SummaryCard } from "@/components/PeriodPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAY_TONE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  unpaid: "bg-red-50 text-red-700 border-red-200",
};
const VAL_TONE = {
  validated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  disputed: "bg-red-50 text-red-700 border-red-200",
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const blank = () => ({
  client_id: "",
  manager_id: "",
  issue_date: today(),
  due_date: plusDays(15),
  period_start: "",
  period_end: "",
  currency: "INR",
  items: [{ description: "", amount: 0 }],
  tax_percent: 18,
  paid_amount: 0,
  status: "draft",
  payment_status: "unpaid",
  validation_status: "pending",
  received_by: "",
  payment_method: "Bank Transfer",
  payment_date: "",
  notes: "",
});

export default function Invoices() {
  const { masters, filters, nameMap } = useData();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [pay, setPay] = useState("all");
  const [val, setVal] = useState("all");
  const [period, setPeriod] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [receivedBy, setReceivedBy] = useState("all");
  const [summary, setSummary] = useState(null);
  const [receivers, setReceivers] = useState([]);
  const [fxRate, setFxRate] = useState(0);
  const [sending, setSending] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ client_id: "", start: filters.start, end: filters.end, currency: "INR", tax_percent: 18 });

  const load = useCallback(async () => {
    const params = {
      period,
      start,
      end,
      client_id: clientFilter,
      payment_status: pay,
      validation_status: val,
      received_by: receivedBy,
    };
    const [list, sum, recv] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/invoices/summary", { params }),
      api.get("/invoices/receivers"),
    ]);
    setRows(list.data);
    setSummary(sum.data);
    setReceivers(recv.data);
  }, [pay, val, period, start, end, clientFilter, receivedBy]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    api.get("/settings/fx").then((r) => setFxRate(Number(r.data.usd_to_inr) || 0)).catch(() => {});
  }, []);

  const sendReminder = async (iv) => {
    setSending(iv.id);
    try {
      const { data } = await api.post(`/invoices/${iv.id}/remind`);
      toast.success(`Payment reminder sent to ${data.to}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(null);
    }
  };

  const subtotal = form.items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const total = subtotal + (subtotal * Number(form.tax_percent || 0)) / 100;

  const save = async () => {
    if (!form.client_id) return toast.error("Select a client");
    const payload = {
      ...form,
      tax_percent: Number(form.tax_percent) || 0,
      paid_amount: Number(form.paid_amount) || 0,
      items: form.items.map((i) => ({ description: i.description, amount: Number(i.amount) || 0 })),
    };
    try {
      if (editId) await api.put(`/invoices/${editId}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editId ? "Invoice updated" : "Invoice created");
      setOpen(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const generate = async () => {
    if (!gen.client_id) return toast.error("Select a client");
    try {
      await api.post("/invoices/generate-from-entries", gen);
      toast.success("Invoice generated from entries");
      setGenOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const download = async (iv) => {
    try {
      const res = await api.get(`/invoices/${iv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${iv.invoice_no}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const saveNote = async (iv, notes) => {
    if ((iv.notes || "") === notes) return;
    try {
      await api.patch(`/invoices/${iv.id}/status`, { notes });
      toast.success("Note saved");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="invoices-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Billing &amp; Collections
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Invoices</h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="generate-invoice-button"
            className="rounded-md border-slate-300 active:scale-95"
            onClick={() => setGenOpen(true)}
          >
            <Wand2 className="mr-2 h-4 w-4" /> From Entries
          </Button>
          <Button
            data-testid="create-invoice-button"
            className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
            onClick={() => {
              setEditId(null);
              setForm(blank());
              setOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Invoice
          </Button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          label="Total"
          value={money(summary?.total)}
          sub={`${summary?.count || 0} invoices · ${fxNote(summary?.total, "INR", fxRate)}`}
          testid="invoices-total"
        />
        <SummaryCard
          label="Total Received"
          value={money(summary?.total_received)}
          sub={fxNote(summary?.total_received, "INR", fxRate)}
          tone="text-emerald-600"
          testid="invoices-total-received"
        />
        <SummaryCard
          label="Total Balance"
          value={money(summary?.total_balance)}
          sub={fxNote(summary?.total_balance, "INR", fxRate)}
          tone={(summary?.total_balance || 0) > 0 ? "text-red-600" : "text-slate-900"}
          testid="invoices-total-balance"
        />
      </div>

      <div
        className="mb-8 grid grid-cols-2 gap-3 border border-slate-200 bg-slate-50 p-4 md:grid-cols-4 lg:grid-cols-6"
        data-testid="invoice-filters"
      >
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          start={start}
          end={end}
          setStart={setStart}
          setEnd={setEnd}
          testid="invoices-period"
        />
        <Field label="Client">
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="filter-invoice-client" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All Clients</SelectItem>
              {masters.clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Payment received by">
          <Select value={receivedBy} onValueChange={setReceivedBy}>
            <SelectTrigger data-testid="filter-received-by" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">Anyone</SelectItem>
              {receivers.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Payment">
          <Select value={pay} onValueChange={setPay}>
            <SelectTrigger data-testid="filter-payment-status" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              {["all", "unpaid", "partial", "paid"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Validation">
          <Select value={val} onValueChange={setVal}>
            <SelectTrigger data-testid="filter-invoice-validation" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              {["all", "pending", "validated", "disputed"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[1400px] text-sm" data-testid="invoices-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Invoice</th>
              <th className="px-3 py-3">Client</th>
              <th className="px-3 py-3">Issued</th>
              <th className="px-3 py-3">Due</th>
              <th className="px-3 py-3 text-right">Total</th>
              <th className="px-3 py-3 text-right">Received</th>
              <th className="px-3 py-3 text-right">Balance</th>
              <th className="px-3 py-3">Payment</th>
              <th className="px-3 py-3">Received By</th>
              <th className="px-3 py-3">Validation</th>
              <th className="px-3 py-3">Note</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`invoice-row-${r.id}`}>
                <td className="px-3 py-2 font-semibold">{r.invoice_no}</td>
                <td className="px-3 py-2">{nameMap[r.client_id] || "—"}</td>
                <td className="px-3 py-2">{r.issue_date}</td>
                <td className="px-3 py-2">{r.due_date}</td>
                <td className="px-3 py-2 text-right font-semibold">{money(r.total, r.currency)}</td>
                <td className="px-3 py-2 text-right">{money(r.paid_amount, r.currency)}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.due_amount > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {money(r.due_amount, r.currency)}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`rounded-md border ${PAY_TONE[r.payment_status]}`}>
                    {r.payment_status}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {r.received_by || "—"}
                  {r.payment_method && <span className="ml-1 text-xs text-slate-500">({r.payment_method})</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`rounded-md border ${VAL_TONE[r.validation_status]}`}>
                    {r.validation_status}
                  </Badge>
                </td>
                <td className="px-3 py-2 min-w-[240px]">
                  <Input
                    data-testid={`invoice-note-${r.id}`}
                    defaultValue={r.notes || ""}
                    placeholder="Add a payment note…"
                    className="h-9 border-transparent bg-transparent px-2 hover:border-slate-300 focus:border-slate-900 focus:bg-white"
                    onBlur={(e) => saveNote(r, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {r.due_amount > 0 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        title={r.last_reminder_at ? `Last reminded ${new Date(r.last_reminder_at).toLocaleString()}` : "Send payment reminder"}
                        data-testid={`remind-invoice-${r.id}`}
                        disabled={sending === r.id}
                        className="h-8 w-8 text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                        onClick={() => sendReminder(r)}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`download-invoice-${r.id}`}
                      className="h-8 w-8"
                      onClick={() => download(r)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`edit-invoice-${r.id}`}
                      className="h-8 w-8"
                      onClick={() => {
                        setEditId(r.id);
                        setForm({ ...blank(), ...r, items: r.items?.length ? r.items : [{ description: "", amount: 0 }] });
                        setOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`delete-invoice-${r.id}`}
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                      onClick={() => remove(r.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center text-slate-500">
                  No invoices yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {editId ? "Edit Invoice" : "New Invoice"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Set the billing period, line items, tax, payment status and client validation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="invoice-client-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {masters.clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Manager</Label>
              <Select value={form.manager_id || ""} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                <SelectTrigger data-testid="invoice-manager-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {masters.managers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {[
              ["issue_date", "Issue Date"],
              ["due_date", "Due Date"],
              ["period_start", "Period Start"],
              ["period_end", "Period End"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs font-bold uppercase">{label}</Label>
                <Input
                  type="date"
                  data-testid={`invoice-${k}-input`}
                  className="mt-1 h-10 border-slate-300"
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}
            <div>
              <Label className="text-xs font-bold uppercase">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="invoice-currency-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  <SelectItem value="INR">INR ₹</SelectItem>
                  <SelectItem value="USD">USD $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Tax %</Label>
              <Input
                type="number"
                data-testid="invoice-tax-input"
                className="mt-1 h-10 border-slate-300"
                value={form.tax_percent}
                onChange={(e) => setForm({ ...form, tax_percent: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Paid Amount</Label>
              <Input
                type="number"
                data-testid="invoice-paid-input"
                className="mt-1 h-10 border-slate-300"
                value={form.paid_amount}
                onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment Status</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger data-testid="invoice-payment-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {["unpaid", "partial", "paid"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Client Validation</Label>
              <Select value={form.validation_status} onValueChange={(v) => setForm({ ...form, validation_status: v })}>
                <SelectTrigger data-testid="invoice-validation-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {["pending", "validated", "disputed"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment received by</Label>
              <Input
                data-testid="invoice-received-by-input"
                placeholder="Person or account"
                className="mt-1 h-10 border-slate-300"
                value={form.received_by || ""}
                onChange={(e) => setForm({ ...form, received_by: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment method</Label>
              <Select
                value={form.payment_method || "Bank Transfer"}
                onValueChange={(v) => setForm({ ...form, payment_method: v })}
              >
                <SelectTrigger data-testid="invoice-method-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {["Bank Transfer", "UPI", "Cash", "Card", "Cheque", "Other"].map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment date</Label>
              <Input
                type="date"
                data-testid="invoice-payment-date-input"
                className="mt-1 h-10 border-slate-300"
                value={form.payment_date || ""}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="invoice-status-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {["draft", "sent", "paid", "overdue", "cancelled"].map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-2">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Line Items
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} className="mb-2 flex gap-2">
                <Input
                  data-testid={`invoice-item-desc-${idx}`}
                  placeholder="Description"
                  className="h-10 border-slate-300"
                  value={it.description}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx] = { ...it, description: e.target.value };
                    setForm({ ...form, items });
                  }}
                />
                <Input
                  data-testid={`invoice-item-amount-${idx}`}
                  type="number"
                  placeholder="Amount"
                  className="h-10 w-40 border-slate-300 text-right"
                  value={it.amount}
                  onChange={(e) => {
                    const items = [...form.items];
                    items[idx] = { ...it, amount: e.target.value };
                    setForm({ ...form, items });
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  data-testid={`remove-item-${idx}`}
                  className="h-10 w-10 text-red-600"
                  onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              data-testid="add-item-button"
              className="rounded-md border-slate-300"
              onClick={() => setForm({ ...form, items: [...form.items, { description: "", amount: 0 }] })}
            >
              <Plus className="mr-1 h-4 w-4" /> Add item
            </Button>
            <div className="mt-4 flex justify-end gap-8 border-t border-slate-200 pt-4 text-sm">
              <span className="text-slate-500">Subtotal {money(subtotal, form.currency)}</span>
              <span className="font-bold" data-testid="invoice-total-preview">
                Total {money(total, form.currency)}
                <span className="ml-2 font-normal text-slate-500" data-testid="invoice-total-fx">
                  {fxNote(total, form.currency, fxRate)}
                </span>
              </span>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase">Notes</Label>
            <Textarea
              data-testid="invoice-notes-input"
              className="mt-1 border-slate-300"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <DialogFooter>
            <Button
              data-testid="save-invoice-button"
              className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
              onClick={save}
            >
              Save Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg border border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              Generate from Entries
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Builds a draft invoice with one line item per campaign for the selected period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase">Client</Label>
              <Select value={gen.client_id} onValueChange={(v) => setGen({ ...gen, client_id: v })}>
                <SelectTrigger data-testid="gen-client-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {masters.clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase">From</Label>
                <Input
                  type="date"
                  data-testid="gen-start-input"
                  className="mt-1 h-10 border-slate-300"
                  value={gen.start}
                  onChange={(e) => setGen({ ...gen, start: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">To</Label>
                <Input
                  type="date"
                  data-testid="gen-end-input"
                  className="mt-1 h-10 border-slate-300"
                  value={gen.end}
                  onChange={(e) => setGen({ ...gen, end: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="gen-submit-button"
              className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
              onClick={generate}
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
